import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Form, Input, InputNumber, Select, Button, Switch, Card, Space, Radio,
  TimePicker, DatePicker, Spin, message, Alert
} from 'antd';
import { SaveOutlined, EyeOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../../services/api';
import ConditionBuilder from '../../components/ConditionBuilder';
import CsvUpload from '../../components/CsvUpload';
import { Loading } from '../../components/UI';

const { Option } = Select;
const { TextArea } = Input;

const TIMEZONES = Intl.supportedValuesOf('timeZone');

const TimezoneFormItem = () => (
  <Form.Item name="timezone" label="Timezone" rules={[{ required: true }]}>
    <Select showSearch>
      {TIMEZONES.map(tz => <Option key={tz} value={tz}>{tz}</Option>)}
    </Select>
  </Form.Item>
);

const BailForm = () => {
  const { bailId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [showSql, setShowSql] = useState(false);
  const [timing, setTiming] = useState('immediate');
  const [userId, setUserId] = useState(null);
  const [bailType, setBailType] = useState('conditions');
  const [userList, setUserList] = useState([]);
  // A user list may carry conditions that narrow it to who matches at each run.
  const [listHasConditions, setListHasConditions] = useState(false);

  const isEdit = !!bailId;

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    // When switching to user_list mode, default to absolute timing with current datetime
    if (bailType === 'user_list' && !isEdit) {
      form.setFieldsValue({
        timing: 'absolute',
        datetime: moment(),
        timezone: 'UTC',
      });
      setTiming('absolute');
    }
  }, [bailType, isEdit, form]);

  useEffect(() => {
    // When switching modes, clear fields that shouldn't be validated in the new mode.
    // conditions stay: a user list can carry them too.
    if (bailType === 'user_list') {
      form.setFieldsValue({
        destination_form: undefined,
      });
    }
  }, [bailType, form]);

  const loadUser = async () => {
    try {
      const res = await api.fetcher({ path: '/users', method: 'POST', body: {} });
      const user = await res.json();
      setUserId(user.id);
      if (isEdit) {
        await loadBail(user.id);
      } else {
        const duplicate = location.state && location.state.duplicateFrom;
        if (duplicate) {
          const def = duplicate.definition || {};
          const execution = def.execution || {};
          const action = def.action || {};

          const defType = def.type || 'conditions';
          setBailType(defType);
          if (defType === 'user_list' && def.user_list) {
            setUserList(def.user_list.users || []);
            setListHasConditions(!!def.conditions);
          }

          setTiming(execution.timing || 'immediate');

          form.setFieldsValue({
            name: `Copy of ${duplicate.name}`,
            description: duplicate.description || '',
            enabled: false,
            conditions: def.conditions,
            timing: execution.timing || 'immediate',
            time_of_day: execution.time_of_day ? moment(execution.time_of_day, 'HH:mm') : null,
            timezone: execution.timezone || 'UTC',
            tolerance_minutes: execution.tolerance_minutes != null ? execution.tolerance_minutes : 30,
            datetime: execution.datetime ? moment(execution.datetime) : null,
            destination_form: action.destination_form || '',
            metadata: action.metadata ? JSON.stringify(action.metadata, null, 2) : '',
          });
        }
        setLoading(false);
      }
    } catch (err) {
      message.error('Failed to load user');
      console.error(err);
      setLoading(false);
    }
  };

  const loadBail = async (uid) => {
    try {
      const res = await api.fetcher({ path: `/users/${uid}/bails/${bailId}` });
      const data = await res.json();
      const bail = data.bail;
      const def = bail.definition;

      // Set timing state for conditional field rendering
      var execution = def.execution || {};
      var action = def.action || {};
      setTiming(execution.timing || 'immediate');

      // Detect bail type and set state accordingly
      const bailDefType = def.type || 'conditions';
      setBailType(bailDefType);
      if (bailDefType === 'user_list' && def.user_list) {
        setUserList(def.user_list.users || []);
        setListHasConditions(!!def.conditions);
      }

      // Populate form
      form.setFieldsValue({
        name: bail.name,
        description: bail.description,
        enabled: bail.enabled,
        conditions: def.conditions,
        timing: execution.timing || 'immediate',
        time_of_day: execution.time_of_day ? moment(execution.time_of_day, 'HH:mm') : null,
        timezone: execution.timezone || 'UTC',
        tolerance_minutes: execution.tolerance_minutes != null ? execution.tolerance_minutes : 30,
        datetime: execution.datetime ? moment(execution.datetime) : null,
        destination_form: action.destination_form,
        metadata: action.metadata ? JSON.stringify(action.metadata, null, 2) : '',
      });
    } catch (err) {
      message.error('Failed to load bail');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const buildDefinition = (values) => {
    const execution = {
      timing: values.timing,
    };

    if (values.timing === 'scheduled') {
      if (values.time_of_day) {
        execution.time_of_day = values.time_of_day.format('HH:mm');
      }
      execution.timezone = values.timezone || 'UTC';
      if (values.tolerance_minutes != null) {
        execution.tolerance_minutes = values.tolerance_minutes;
      }
    } else if (values.timing === 'absolute') {
      if (values.datetime) {
        execution.datetime = values.datetime.format('YYYY-MM-DDTHH:mm:ss');
      }
      execution.timezone = values.timezone || 'UTC';
    }

    let metadata = {};
    if (values.metadata) {
      try {
        metadata = JSON.parse(values.metadata);
      } catch (e) {
        // Invalid JSON, use empty object
      }
    }

    if (bailType === 'user_list') {
      const conditions = listHasConditions ? form.getFieldValue('conditions') : undefined;
      return {
        type: 'user_list',
        // Each user's platform is resolved server-side from their pageid's
        // messaging credential; it cannot be set here.
        user_list: { users: userList },
        ...(conditions ? { conditions } : {}),
        execution,
        action: {
          destination_form: userList.length > 0 ? userList[0].shortcode : '',
          metadata,
        },
      };
    }

    return {
      type: 'conditions',
      conditions: values.conditions,
      execution,
      action: {
        destination_form: values.destination_form,
        metadata,
      },
    };
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      // Validate that user_list mode has users uploaded
      if (bailType === 'user_list' && userList.length === 0) {
        message.error('Please upload a CSV file with at least one user');
        setSaving(false);
        return;
      }

      if (bailType === 'user_list' && values.timing !== 'absolute' && !listHasConditions) {
        message.error(
          'A recurring user list needs conditions, or every run moves the whole list again. '
          + 'Add conditions or use absolute timing.',
        );
        setSaving(false);
        return;
      }

      const definition = buildDefinition(values);
      const body = {
        name: values.name,
        description: values.description,
        definition,
        enabled: values.enabled !== undefined && values.enabled !== null ? values.enabled : false,
      };

      if (isEdit) {
        await api.fetcher({
          path: `/users/${userId}/bails/${bailId}`,
          method: 'PUT',
          body,
        });
        message.success('Bail updated successfully');
      } else {
        await api.fetcher({
          path: `/users/${userId}/bails`,
          method: 'POST',
          body,
        });
        message.success('Bail created successfully');
      }
      history.push('/bails');
    } catch (err) {
      message.error(`Failed to ${isEdit ? 'update' : 'create'} bail: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const values = form.getFieldsValue();
      const definition = buildDefinition(values);

      const res = await api.fetcher({
        path: `/users/${userId}/bails/preview`,
        method: 'POST',
        body: { definition },
      });
      const result = await res.json();
      setPreviewResult(result);
    } catch (err) {
      message.error(`Preview failed: ${err.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) return <Loading>Loading...</Loading>;

  return (
    <div style={{ padding: '30px', maxWidth: 900 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => history.push('/bails')}
        style={{ marginBottom: 16 }}
      >
        Back to Bail Systems
      </Button>

      <h2>{isEdit ? 'Edit' : 'Create'} Bail System</h2>

      <Spin spinning={saving}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            enabled: false,
            timing: 'immediate',
            timezone: 'UTC',
            tolerance_minutes: 30,
            conditions: { type: 'form', value: '' },
          }}
        >
          {/* Basic Info */}
          <Card title="Basic Information" style={{ marginBottom: 16 }}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input placeholder="e.g., 4-week dropout recovery" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <TextArea rows={2} placeholder="Describe what this bail system does..." />
            </Form.Item>

            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Card>

          {/* Bail Type */}
          <Card title="Bail Type" style={{ marginBottom: 16 }}>
            <Radio.Group value={bailType} onChange={(e) => setBailType(e.target.value)}>
              <Radio.Button value="conditions">Conditions</Radio.Button>
              <Radio.Button value="user_list">User List (CSV)</Radio.Button>
            </Radio.Group>
          </Card>

          {/* Conditions */}
          {bailType === 'conditions' && (
            <Card title="Conditions" style={{ marginBottom: 16 }}>
              <p style={{ color: '#666', marginBottom: 16 }}>
                Define which users should be bailed. You can combine multiple conditions using AND/OR logic.
              </p>
              <Form.Item
                name="conditions"
                rules={bailType === 'conditions' ? [{ required: true, message: 'Please define at least one condition' }] : []}
              >
                <ConditionBuilder />
              </Form.Item>
            </Card>
          )}

          {/* User List */}
          {bailType === 'user_list' && (
            <Card title="User List" style={{ marginBottom: 16 }}>
              <p style={{ color: '#666', marginBottom: 16 }}>
                Upload a CSV file with user IDs, page IDs, and destination forms. Each user can be bailed to a different form.
              </p>
              <CsvUpload value={userList} onChange={setUserList} />
            </Card>
          )}

          {bailType === 'user_list' && (
            <Card title="List Conditions" style={{ marginBottom: 16 }}>
              <Space align="center" style={{ marginBottom: 16 }}>
                <Switch checked={listHasConditions} onChange={setListHasConditions} />
                <span>Only move listed users who match these conditions</span>
              </Space>
              <p style={{ color: '#666', marginBottom: 16 }}>
                The list decides who can be moved; the conditions decide when. Each run
                moves only the listed users who match at that moment, so someone already
                moved (who has left the form) is not moved again. Required for scheduled
                and immediate timing.
              </p>
              {listHasConditions && (
                <Form.Item
                  name="conditions"
                  rules={[{ required: true, message: 'Please define at least one condition' }]}
                >
                  <ConditionBuilder />
                </Form.Item>
              )}
            </Card>
          )}

          {/* Execution Timing */}
          <Card title="Execution Timing" style={{ marginBottom: 16 }}>
            <Form.Item
              name="timing"
              label="Timing Mode"
              rules={[{ required: true }]}
            >
              <Select onChange={setTiming}>
                <Option value="immediate">Immediate (every execution cycle)</Option>
                <Option value="scheduled">Scheduled (daily at specific time)</Option>
                <Option value="absolute">Absolute (one-time at specific datetime)</Option>
              </Select>
            </Form.Item>

            {timing === 'scheduled' && (
              <>
                <Form.Item
                  name="time_of_day"
                  label="Time of Day"
                  rules={[{ required: true, message: 'Please select a time' }]}
                >
                  <TimePicker format="HH:mm" />
                </Form.Item>

                <TimezoneFormItem />

                <Form.Item
                  name="tolerance_minutes"
                  label="Tolerance (minutes)"
                  tooltip="How many minutes after the scheduled time the executor can still fire this bail. Increase this if the executor job may be delayed or run infrequently."
                  rules={[{ required: true, type: 'number', min: 0, max: 1440 }]}
                >
                  <InputNumber min={0} max={1440} style={{ width: 120 }} addonAfter="min" />
                </Form.Item>
              </>
            )}

            {timing === 'absolute' && (
              <>
                <Form.Item
                  name="datetime"
                  label="Execute At"
                  rules={[{ required: true, message: 'Please select a datetime' }]}
                >
                  <DatePicker showTime format="YYYY-MM-DD HH:mm" />
                </Form.Item>

                <TimezoneFormItem />
              </>
            )}
          </Card>

          {/* Action */}
          {bailType === 'conditions' && (
            <Card title="Action" style={{ marginBottom: 16 }}>
              <Form.Item
                name="destination_form"
                label="Destination Form"
                rules={bailType === 'conditions' ? [{ required: true, message: 'Please enter the destination form' }] : []}
                extra="The form/survey shortcode to redirect bailed users to"
              >
                <Input placeholder="e.g., exit_survey_v2" />
              </Form.Item>

              <Form.Item
                name="metadata"
                label="Metadata (JSON)"
                extra="Optional JSON metadata to include with the bail event"
              >
                <TextArea
                  rows={3}
                  placeholder='{"reason": "dropout_4weeks", "version": 1}'
                />
              </Form.Item>
            </Card>
          )}

          {/* Preview */}
          <Card title="Preview" style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 16 }}>
              Preview which users would be bailed with the current conditions (dry run).
            </p>
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              loading={previewing}
            >
              Preview Matching Users
            </Button>

            {previewResult && (
              <>
                <Alert
                  style={{ marginTop: 16 }}
                  type={previewResult.count > 0 ? 'info' : 'warning'}
                  message={`${previewResult.count} users would be bailed`}
                  description={
                    previewResult.count > 0 ? (
                      <div>
                        <p>Sample users:</p>
                        <ul>
                          {previewResult.users.slice(0, 5).map((u, i) => (
                            <li key={i}>{u.userid} (page: {u.pageid})</li>
                          ))}
                          {previewResult.count > 5 && <li>... and {previewResult.count - 5} more</li>}
                        </ul>
                      </div>
                    ) : 'No users match the current conditions.'
                  }
                />
                {previewResult.sql && (
                  <div style={{ marginTop: 12 }}>
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingLeft: 0 }}
                      onClick={() => setShowSql(s => !s)}
                    >
                      {showSql ? 'Hide SQL' : 'Show SQL'}
                    </Button>
                    {showSql && (
                      <div style={{ marginTop: 4 }}>
                        <pre style={{
                          background: '#f5f5f5',
                          border: '1px solid #d9d9d9',
                          borderRadius: 4,
                          padding: '10px 12px',
                          fontSize: 12,
                          lineHeight: 1.5,
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          margin: 0,
                        }}>
                          {previewResult.sql}
                        </pre>
                        {previewResult.params && previewResult.params.length > 0 && (
                          <div style={{ marginTop: 8, fontSize: 12, color: '#595959' }}>
                            <strong>Parameters: </strong>
                            {previewResult.params.map((p, i) => (
                              <span key={i} style={{ marginRight: 12 }}>
                                <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 2 }}>
                                  {'$'}{i + 1}
                                </code>
                                {' = '}
                                <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 2 }}>
                                  {JSON.stringify(p)}
                                </code>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Submit */}
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              {isEdit ? 'Update' : 'Create'} Bail System
            </Button>
            <Button onClick={() => history.push('/bails')}>
              Cancel
            </Button>
          </Space>
        </Form>
      </Spin>
    </div>
  );
};

export default BailForm;
